function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

import React, { forwardRef } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { Text4 } from "@churchofjesuschrist/eden-text";
import { H4, H6 } from "@churchofjesuschrist/eden-headings";
import { grey10, info40, red30, lineHeightTight, spacing4, spacing8, spacing16, text120 } from "@churchofjesuschrist/eden-style-constants";
import AspectRatio from "@churchofjesuschrist/eden-aspect-ratio";
import Image from "@churchofjesuschrist/eden-image";
import parse from "html-react-parser"; //////////////////////////////////
// Styled Component Definitions //
//////////////////////////////////
// This is necessary for styling hover states

const Wrapper = styled.a.withConfig({
  componentId: "sc-omeqik-0"
})(["&&&{color:", ";text-decoration:none;&:hover{color:", ";}}"], text120, text120);
const FlexWrapper = styled(_ref => {
  let { ...props
  } = _ref;
  delete props.alignment;
  return /*#__PURE__*/React.createElement("div", props);
}).withConfig({
  componentId: "sc-omeqik-1"
})(["display:flex;justify-content:space-between;flex-direction:", ";"], _ref2 => {
  let {
    alignment
  } = _ref2;
  return alignment === "end" ? "row" : "row-reverse";
});
const Text = styled.div.withConfig({
  componentId: "sc-omeqik-2"
})(["min-height:39px;flex-grow:1;flex-basis:0;display:flex;flex-direction:column;overflow:hidden;"]);
const MetaContent = styled.div.withConfig({
  componentId: "sc-omeqik-3"
})(["display:flex;align-items:center;margin-bottom:", ";"], spacing4);
const Meta = styled.div.withConfig({
  componentId: "sc-omeqik-4"
})(["display:flex;white-space:nowrap;position:relative;flex-shrink:0;line-height:1;* + &{margin-inline-start:", ";}"], spacing4);
const PrimaryMeta = styled(H6).withConfig({
  componentId: "sc-omeqik-5"
})(["color:", ";line-height:", ";display:inline-block;vertical-align:top;max-width:100%;overflow:hidden;text-overflow:ellipsis;"], _ref3 => {
  let {
    color
  } = _ref3;
  return color ? color : red30;
}, lineHeightTight);
const SecondaryMeta = styled(H6).withConfig({
  componentId: "sc-omeqik-6"
})(["line-height:", ";flex:1;display:inline-block;vertical-align:top;max-width:100%;overflow:hidden;text-overflow:ellipsis;", " + &{&:before{content:\" | \";white-space:pre;}}"], lineHeightTight, PrimaryMeta);
const TitleWrapper = styled.div.withConfig({
  componentId: "sc-omeqik-7"
})(["flex-shrink:0;margin-bottom:", ";position:relative;"], spacing8);
const Description = styled(_ref4 => {
  let { ...props
  } = _ref4;
  delete props.emphasized;
  return /*#__PURE__*/React.createElement("div", props);
}).withConfig({
  componentId: "sc-omeqik-8"
})(["display:-webkit-box;-webkit-box-orient:vertical;-webkit-line-clamp:", ";overflow:hidden;"], _ref5 => {
  let {
    emphasized
  } = _ref5;
  return emphasized ? "6" : "4";
});
const Title = styled(H4).withConfig({
  componentId: "sc-omeqik-9"
})(["display:-webkit-box;-webkit-box-orient:vertical;-webkit-line-clamp:", ";overflow:hidden;", "[href]:hover &{color:", ";}"], _ref6 => {
  let {
    hasDescription,
    emphasized
  } = _ref6;
  return hasDescription ? "2" : emphasized ? "5" : "3";
}, Wrapper, info40);
const StyledAspectWrapper = styled(_ref7 => {
  let { ...props
  } = _ref7;
  delete props.emphasized;
  delete props.ratio;
  return /*#__PURE__*/React.createElement("div", props);
}).withConfig({
  componentId: "sc-omeqik-10"
})(["", ";"], _ref8 => {
  let {
    ratio,
    emphasized
  } = _ref8;
  return `
      flex-basis: ${ratio === "1:1" ? emphasized ? "45%" : "30%" : emphasized ? "48%" : "40%"};
      min-width: ${ratio === "1:1" ? emphasized ? "132px" : "88px" : "0"};
      ${ratio === "1:1" ? emphasized ? "max-width: 192px;" : "max-width: 128px;" : `max-width: calc((100% - 420px) * 9999);
        max-width: min(calc((100% - 420px) * 9999), ${emphasized ? "342px" : "300px"});`};
  `;
});
const StyledRatio = styled(_ref9 => {
  let { ...props
  } = _ref9;
  delete props.alignment;
  delete props.emphasized;
  return /*#__PURE__*/React.createElement(AspectRatio, props);
}).withConfig({
  componentId: "sc-omeqik-11"
})(["", ";"], _ref10 => {
  let {
    alignment
  } = _ref10;
  return `margin-inline-start: ${alignment === "start" ? "0" : spacing16};
     margin-inline-end: ${alignment === "end" ? "0" : spacing16};
  `;
});
const StyledImage = styled(Image).withConfig({
  componentId: "sc-omeqik-12"
})(["display:block;width:100%;height:100%;object-fit:cover;flex-shrink:0;", "[href]:hover &{filter:brightness(0.9);}"], Wrapper);
const HR = styled.hr.withConfig({
  componentId: "sc-omeqik-13"
})(["margin:", " 0;border:none;border-bottom:1px solid ", ";width:100%;height:1px;", ":last-of-type &{display:none;}"], spacing16, grey10, Wrapper); //////////////////////////////////
//    Beginning of Component    //
//////////////////////////////////

const ListTile = /*#__PURE__*/forwardRef((_ref11, ref) => {
  let {
    alignment = "end",
    children,
    description,
    hideRule = false,
    emphasized = false,
    image: {
      alt,
      aspectRatio = "1:1",
      focalPoint,
      src,
      srcSet,
      sizes
    } = {},
    primaryMeta,
    secondaryMeta,
    theme: {
      primaryColor
    } = {},
    title,
    badge,
    ...rest
  } = _ref11;
  return /*#__PURE__*/React.createElement(Wrapper, _extends({
    ref: ref
  }, rest), /*#__PURE__*/React.createElement(FlexWrapper, {
    alignment: alignment
  }, /*#__PURE__*/React.createElement(Text, null, primaryMeta || secondaryMeta || badge ? /*#__PURE__*/React.createElement(MetaContent, null, badge ? badge : null, (primaryMeta || secondaryMeta) && /*#__PURE__*/React.createElement(Meta, null, primaryMeta && /*#__PURE__*/React.createElement(PrimaryMeta, {
    color: primaryColor
  }, primaryMeta), secondaryMeta && /*#__PURE__*/React.createElement(SecondaryMeta, null, secondaryMeta))) : null, /*#__PURE__*/React.createElement(TitleWrapper, null, /*#__PURE__*/React.createElement(Title, {
    hasDescription: !!description,
    emphasized: emphasized
  }, title)), description && /*#__PURE__*/React.createElement(Description, {
    emphasized: emphasized
  }, /*#__PURE__*/React.createElement(Text4, null, typeof description === "string" ? parse(description) : description), children)), src && /*#__PURE__*/React.createElement(StyledAspectWrapper, {
    ratio: aspectRatio,
    emphasized: emphasized,
    "data-aspect-wrapper": true
  }, /*#__PURE__*/React.createElement(StyledRatio, {
    ratio: aspectRatio,
    alignment: alignment,
    emphasized: emphasized
  }, /*#__PURE__*/React.createElement(StyledImage, {
    alt: alt,
    focalPoint: focalPoint,
    src: src,
    srcSet: srcSet,
    sizes: sizes
  })))), !hideRule && /*#__PURE__*/React.createElement(HR, null));
});
ListTile.displayName = "ListTile";
ListTile.propTypes = {
  /** Large text title of tile */
  title: PropTypes.node.isRequired,

  /** sub title or primary meta, can be colored with theme */
  primaryMeta: PropTypes.node,

  /** secondary meta data */
  secondaryMeta: PropTypes.node,

  /** description of tile content */
  description: PropTypes.node,

  /** image to display next to description. aspectRatio should be one of 1:1 or 16:9 and will default to 1:1 */
  image: PropTypes.shape({
    /** url of media location */
    src: PropTypes.string.isRequired,

    /** srcSet of image */
    srcSet: PropTypes.string,

    /** sizes for srcSet */
    sizes: PropTypes.string,

    /** alternate image text */
    alt: PropTypes.string.isRequired,

    /** ratio to display media at. Design specs specify only 1:1 and 16:9 as valid values, defaults to "1:1" */
    aspectRatio: PropTypes.oneOf(["1:1", "16:9"]),

    /** focal point coordinates for image */
    focalPoint: PropTypes.shape({
      x: PropTypes.string.isRequired,
      y: PropTypes.string.isRequired
    })
  }),

  /** emphasized tiles show larger than other tiles in the list */
  emphasized: PropTypes.bool,

  /** define alignment of media, defaults to "end" */
  alignment: PropTypes.oneOf(["start", "end"]),

  /** theme allows for setting a primary color, which only changes the primary meta in this component */
  theme: PropTypes.shape({
    primaryColor: PropTypes.string.isRequired
  }),

  /** link to go to when clicked */
  href: PropTypes.string,

  /** used to manually hide rule under tile, like if breaking a list of tiles into columns. Defaults to false. */
  hideRule: PropTypes.bool,

  /** An eden-badge element */
  badge: PropTypes.element
};
export default ListTile;